import type { ExtraNavigationTimings, PageSizeMetrics } from './types.js';

export const getPageSizeMetrics = (): PageSizeMetrics => {
	let maxDepth = -1;
	let maxWidth = -1;
	let numElements = 0;

	// As described in the lighthouse DOM audit - https://web.dev/dom-size/
	const calculateDOMsizeAndDepth = (el: Element | ShadowRoot | null = document.body, depth = 1) => {
		if (!el) {
			return;
		}

		if (depth > maxDepth) {
			maxDepth = depth;
		}

		if (el.children.length > maxWidth) {
			maxWidth = el.children.length;
		}

		let child = el.firstElementChild;
		while (child) {
			calculateDOMsizeAndDepth(child, depth + 1);
			// If element has shadow dom, traverse into that tree.
			if (child.shadowRoot) {
				calculateDOMsizeAndDepth(child.shadowRoot, depth + 1);
			}
			child = child.nextElementSibling;

			numElements += 1;
		}
	};

	calculateDOMsizeAndDepth();

	return {
		totalBodyElements: numElements,
		maxNestedLevels: maxDepth,
		maxChildElements: maxWidth,
	};
};

export const getExtraNavigationTimings = (): ExtraNavigationTimings => {
	if (!performance) return {};

	const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;

	if (!navigation) return {};

	return {
		// From https://www.w3.org/TR/navigation-timing-2/
		decodedBodySize: navigation.decodedBodySize,
		encodedBodySize: navigation.encodedBodySize,
		transferSize: navigation.transferSize,
	};
};

const assetsTypes = ['script', 'link'];
export const getPageSizeInfo = ({ start, stop }: { start: number; stop: number }) => {
	const pageSize = getExtraNavigationTimings();
	if (
		!pageSize ||
		Object.keys(pageSize).length === 0 ||
		pageSize.decodedBodySize === 0 ||
		start > 0
	) {
		return {};
	}

	const decoded: Record<string, number> = {
		total: pageSize.decodedBodySize as number,
		pageSize: pageSize.decodedBodySize as number,
		assets: 0,
		endpoints: 0,
	};

	const encoded: Record<string, number> = {
		total: pageSize.encodedBodySize as number,
		pageSize: pageSize.encodedBodySize as number,
		assets: 0,
		endpoints: 0,
	};

	const net: Record<string, number> = {
		total: pageSize.transferSize as number,
		assets: 0,
		endpoints: 0,
	};

	for (const entry of performance.getEntriesByType('resource') as PerformanceResourceTiming[]) {
		if (entry.startTime > stop) {
			// eslint-disable-next-line no-continue
			continue;
		}

		if (assetsTypes.includes(entry.initiatorType)) {
			decoded.total += entry.decodedBodySize;
			decoded.assets += entry.decodedBodySize;
			encoded.total += entry.encodedBodySize;
			encoded.assets += entry.encodedBodySize;
			net.total += entry.transferSize;
			net.assets += entry.transferSize;
		} else {
			decoded.total += entry.decodedBodySize;
			decoded.endpoints += entry.decodedBodySize;
			encoded.total += entry.encodedBodySize;
			encoded.endpoints += entry.encodedBodySize;
			net.total += entry.transferSize;
			net.endpoints += entry.transferSize;
		}
	}

	const returnValue: Record<string, number> = {};
	Object.keys(decoded).forEach((key) => {
		returnValue[`size:decoded:${key}`] = decoded[key];
	});
	Object.keys(encoded).forEach((key) => {
		returnValue[`size:encoded:${key}`] = encoded[key];
	});
	Object.keys(net).forEach((key) => {
		returnValue[`size:net:${key}`] = net[key];
	});

	return returnValue;
};
